/**
 * @fileoverview Comprehensive Input Sanitizer for XSS, SQL Injection, and other attack prevention.
 * Provides client-side validation and sanitization for form inputs with HTMX and Alpine.js integration.
 *
 * @description This module intercepts form submissions and HTMX requests to validate and sanitize
 * user input before it reaches the server. It detects common attack patterns including:
 * - Cross-Site Scripting (XSS)
 * - SQL Injection
 * - Command Injection
 * - Path Traversal
 * - LDAP/XML/NoSQL Injection
 *
 * @example
 * // Skip sanitization on a form
 * <form data-no-sanitize>...</form>
 *
 * // Skip sanitization on a specific input
 * <input type="text" data-no-sanitize name="html_content" />
 *
 * // Skip sanitization on HTMX elements
 * <button hx-post="/api/save" data-no-sanitize>Save Raw</button>
 *
 * @version 1.1.0
 * @license MIT
 */

/**
 * @typedef {Object} SanitizeOptions
 * @property {boolean} [allowHtml=false] - Whether to allow HTML tags (skips HTML escaping)
 * @property {number|null} [maxLength=null] - Maximum allowed string length (null for no limit)
 * @property {number|null} [minLength=null] - Minimum required string length (null for no limit)
 * @property {boolean} [trim=true] - Whether to trim whitespace from start/end
 * @property {boolean} [allowEmpty=true] - Whether to allow empty strings after trimming
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether the input passed validation
 * @property {string} sanitized - The sanitized version of the input (empty string if invalid)
 * @property {string|null} error - Error message if validation failed, null otherwise
 * @property {string} original - The original input value before processing
 */

/**
 * @typedef {Object} FormValidationResult
 * @property {boolean} isValid - Whether all fields passed validation
 * @property {Object<string, ValidationResult>} results - Validation results keyed by field name
 */

/**
 * Input sanitizer object providing validation and sanitization utilities.
 * @namespace InputSanitizer
 */
const InputSanitizer = {
  /**
   * Fields that should NEVER be validated (technical/system fields).
   * These are typically auto-generated tokens, CAPTCHA responses, or framework-specific fields.
   * @type {string[]}
   * @readonly
   */
  excludedFields: [
    "g-recaptcha-response",
    "h-captcha-response",
    "csrf_token",
    "authenticity_token",
    "_token",
    "hx-request",
    "hx-trigger",
    "hx-target",
    "hx-current-url",
    "hx-prompt",
  ],

  /**
   * Determines if a field should be skipped based on its name.
   * Checks against the excludedFields list and common patterns for system fields.
   *
   * @param {string} fieldName - The name attribute of the form field
   * @returns {boolean} True if the field should skip validation
   *
   * @example
   * InputSanitizer.shouldSkipField('csrf_token'); // true
   * InputSanitizer.shouldSkipField('username');    // false
   * InputSanitizer.shouldSkipField('hx-trigger');  // true
   */
  shouldSkipField(fieldName) {
    if (!fieldName) return false;

    if (this.excludedFields.includes(fieldName)) {
      return true;
    }

    /**
     * Patterns that identify system/technical fields:
     * - ^csrf: CSRF token fields
     * - ^_: Private/internal fields (Rails convention)
     * - -response$: CAPTCHA response fields
     * - ^hx-: HTMX internal headers/fields
     * - token$: Any token field
     * @type {RegExp[]}
     */
    const skipPatterns = [/^csrf/i, /^_/, /-response$/i, /^hx-/i, /token$/i];

    return skipPatterns.some((pattern) => pattern.test(fieldName));
  },

  /**
   * Checks if input text contains potentially malicious content.
   * Scans for common attack patterns across multiple injection types.
   *
   * @param {string} text - The text to check for malicious content
   * @returns {boolean} True if malicious patterns are detected
   *
   * @example
   * InputSanitizer.containsMaliciousContent('<script>alert(1)</script>'); // true
   * InputSanitizer.containsMaliciousContent("'; DROP TABLE users; --"); // true
   * InputSanitizer.containsMaliciousContent('Hello World');              // false
   */
  containsMaliciousContent(text) {
    if (typeof text !== "string") return false;

    /**
     * Malicious pattern categories:
     * 1. XSS - Script injection, event handlers, dangerous DOM access
     * 2. SQL Injection - UNION, SELECT, DROP, DELETE, INSERT, UPDATE patterns
     * 3. Command Injection - Shell metacharacters, command substitution
     * 4. Path Traversal - Directory traversal sequences
     * 5. LDAP Injection - LDAP filter syntax abuse
     * 6. XML Injection - CDATA, DOCTYPE, ENTITY declarations
     * 7. NoSQL Injection - MongoDB operator injection
     * @type {RegExp[]}
     */
    const maliciousPatterns = [
      // XSS - Script injection
      /<script[\s>]/i,
      /<\/script>/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /eval\s*\(/i,
      /<img[^>]+onerror/i,
      /document\.cookie/i,
      /document\.write/i,
      /window\.location/i,
      /innerHTML/i,

      // SQL Injection
      /(\bunion\b.*\bselect\b)/i,
      /(\bselect\b.*\bfrom\b.*\bwhere\b)/i,
      /(;\s*drop\s+table)/i,
      /(;\s*delete\s+from)/i,
      /(;\s*insert\s+into)/i,
      /(;\s*update\s+.*\bset\b)/i,
      /('\s*or\s*'1'\s*=\s*'1)/i,
      /('\s*or\s*1\s*=\s*1)/i,
      /(--\s*$)/,
      /\/\*.*\*\//,
      /('\s*;)/,
      /(\bexec\b|\bexecute\b).*\(/i,
      /xp_cmdshell/i,

      // Command Injection - Context-aware patterns (not just single characters)
      /\$\{[^}]+\}/, // ${command} - Variable expansion with content
      /\$\([^)]+\)/, // $(command) - Command substitution with content
      /`[^`]+`/, // `command` - Backtick execution with content
      /;\s*(rm|cat|ls|wget|curl|chmod|chown|mv|cp|mkdir|touch|echo|kill|ps|nc|netcat|bash|sh|zsh|ksh|python|perl|ruby|php|node)\b/i, // Semicolon followed by shell commands
      /\|\s*(bash|sh|zsh|ksh|nc|netcat|python|perl|ruby|php|node)\b/i, // Pipe to shell/interpreter
      /&&\s*(rm|wget|curl|chmod|bash|sh)\b/i, // && followed by dangerous commands
      /\|\|\s*(rm|wget|curl|chmod|bash|sh)\b/i, // || followed by dangerous commands
      />\s*\/dev/i, // Redirect to /dev
      /\/bin\/(bash|sh|zsh|ksh)/i, // Direct shell paths
      /\/etc\/(passwd|shadow)/i, // Sensitive file access

      // Path Traversal
      /\.\.\//,
      /\.\.\\/,
      /%2e%2e/i,

      // LDAP Injection
      /\(\|/,
      /\)\(/,
      /\*\)/,

      // XML Injection
      /<!\[CDATA\[/i,
      /<!DOCTYPE/i,
      /<!ENTITY/i,

      // NoSQL Injection
      /\$where/i,
      /\$ne\b/i,
      /\$gt\b/i,
      /\$regex/i,
    ];

    return maliciousPatterns.some((pattern) => pattern.test(text));
  },

  /**
   * Validates and sanitizes input text.
   * First checks for malicious content, then applies sanitization rules.
   *
   * @param {string} text - The text to validate and sanitize
   * @param {SanitizeOptions} [options={}] - Configuration options for sanitization
   * @returns {ValidationResult} Object containing validation status and sanitized text
   *
   * @example
   * // Basic usage
   * const result = InputSanitizer.validateAndSanitize('  Hello World  ');
   * // { isValid: true, sanitized: 'Hello World', error: null, original: '  Hello World  ' }
   *
   * @example
   * // With options
   * const result = InputSanitizer.validateAndSanitize('Hi', { minLength: 5 });
   * // { isValid: false, sanitized: 'Hi', error: 'Must be at least 5 characters', original: 'Hi' }
   *
   * @example
   * // Malicious input
   * const result = InputSanitizer.validateAndSanitize('<script>alert(1)</script>');
   * // { isValid: false, sanitized: '', error: 'Input contains potentially malicious content', original: '...' }
   */
  validateAndSanitize(text, options = {}) {
    /** @type {ValidationResult} */
    const result = {
      isValid: true,
      sanitized: text,
      error: null,
      original: text,
    };

    if (typeof text !== "string") {
      result.isValid = false;
      result.error = "Invalid input type";
      return result;
    }

    // Check for malicious content BEFORE sanitizing
    if (this.containsMaliciousContent(text)) {
      result.isValid = false;
      result.error = "Input contains potentially malicious content";
      result.sanitized = "";
      return result;
    }

    /** @type {SanitizeOptions} */
    const defaults = {
      allowHtml: false,
      maxLength: null,
      minLength: null,
      trim: true,
      allowEmpty: true,
    };

    const opts = { ...defaults, ...options };
    let sanitized = text;

    // Trim whitespace
    if (opts.trim) {
      sanitized = sanitized.trim();
    }

    // Skip validation for empty fields if allowed
    if (opts.allowEmpty && sanitized.length === 0) {
      result.sanitized = "";
      return result;
    }

    // Check min length (only if specified)
    if (opts.minLength !== null && sanitized.length < opts.minLength) {
      result.isValid = false;
      result.error = `Must be at least ${opts.minLength} characters`;
      return result;
    }

    // Check max length (only if specified and valid)
    if (
      opts.maxLength !== null &&
      opts.maxLength > 0 &&
      sanitized.length > opts.maxLength
    ) {
      result.isValid = false;
      result.error = `Must be less than ${opts.maxLength} characters`;
      return result;
    }

    // HTML entity escaping (unless allowHtml is true)
    if (!opts.allowHtml) {
      sanitized = this.escapeHtml(sanitized);
    }

    result.sanitized = sanitized;
    return result;
  },

  /**
   * Escapes HTML special characters to prevent XSS attacks.
   * Converts dangerous characters to their HTML entity equivalents.
   *
   * @param {string} text - The text to escape
   * @returns {string} The escaped text with HTML entities
   *
   * @example
   * InputSanitizer.escapeHtml('<div>Hello</div>');
   * // '&lt;div&gt;Hello&lt;&#x2F;div&gt;'
   */
  escapeHtml(text) {
    if (typeof text !== "string") return text;

    /** @type {Object<string, string>} */
    const entityMap = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#x27;",
      "/": "&#x2F;",
    };

    return text.replace(/[&<>"'/]/g, (char) => entityMap[char]);
  },

  /**
   * Quick sanitize method for backward compatibility.
   * Returns only the sanitized string, discarding validation metadata.
   *
   * @param {string} text - The text to sanitize
   * @param {SanitizeOptions} [options={}] - Configuration options
   * @returns {string} The sanitized text
   * @deprecated Use validateAndSanitize() for full validation results
   *
   * @example
   * const clean = InputSanitizer.sanitize('  Hello <World>  ');
   * // 'Hello &lt;World&gt;'
   */
  sanitize(text, options = {}) {
    const result = this.validateAndSanitize(text, options);
    return result.sanitized;
  },

  /**
   * Checks if an element or its ancestors have the data-no-sanitize attribute.
   * Used to skip sanitization for specific elements or forms.
   *
   * @param {Element|null} element - The DOM element to check
   * @returns {boolean} True if sanitization should be skipped
   *
   * @example
   * // <div data-no-sanitize><input id="myInput" /></div>
   * InputSanitizer.shouldSkipElement(document.getElementById('myInput')); // true
   */
  shouldSkipElement(element) {
    if (!element) return false;

    // Check the element itself
    if (element.hasAttribute && element.hasAttribute("data-no-sanitize")) {
      return true;
    }

    // Check for closest ancestor with the attribute (for forms/containers)
    if (element.closest && element.closest("[data-no-sanitize]")) {
      return true;
    }

    return false;
  },
};

/**
 * HTMX Integration - Validates ALL POST requests before they are sent.
 * Intercepts the htmx:configRequest event to sanitize form parameters.
 *
 * @listens htmx:configRequest
 *
 * @description
 * This handler:
 * 1. Skips GET requests (searches, filters, pagination)
 * 2. Skips requests from elements with data-no-sanitize attribute
 * 3. Validates each parameter against malicious patterns
 * 4. Sanitizes valid parameters (HTML entity escaping)
 * 5. Blocks the request if validation fails
 *
 * @example
 * // This form will be validated
 * <form hx-post="/api/submit">...</form>
 *
 * // This form will skip validation entirely
 * <form hx-post="/api/submit" data-no-sanitize>...</form>
 *
 * // This button will skip validation
 * <button hx-post="/api/save" data-no-sanitize>Save Raw HTML</button>
 */
document.addEventListener("htmx:configRequest", function (event) {
  // Skip GET requests (searches, filters, pagination)
  if (event.detail.verb !== "post") {
    return;
  }

  // Check if the triggering element has data-no-sanitize attribute
  const triggerElement = event.detail.elt;
  if (InputSanitizer.shouldSkipElement(triggerElement)) {
    console.debug(
      "[InputSanitizer] Skipping sanitization for element with data-no-sanitize",
    );
    return;
  }

  if (event.detail.parameters) {
    let hasError = false;
    /** @type {string[]} */
    const errors = [];

    for (const [key, value] of Object.entries(event.detail.parameters)) {
      // SKIP technical/system fields
      if (InputSanitizer.shouldSkipField(key)) {
        continue;
      }

      if (typeof value === "string") {
        const result = InputSanitizer.validateAndSanitize(value, {
          allowEmpty: true,
        });

        if (!result.isValid) {
          hasError = true;
          errors.push(`${key}: ${result.error}`);
          console.warn(
            `[InputSanitizer] Validation failed for "${key}":`,
            value,
          );
        } else {
          event.detail.parameters[key] = result.sanitized;
        }
      }
    }

    // Block the request if validation failed
    if (hasError) {
      event.preventDefault();
      alert("Form validation failed:\n" + errors.join("\n"));
    }
  }
});

/**
 * Alpine.js Integration - Provides validation utilities as magic helpers and stores.
 *
 * @listens alpine:init
 *
 * @description
 * Registers:
 * - `$validateSanitize` magic helper for inline validation
 * - `validator` store with validate() and validateForm() methods
 *
 * @example
 * // Using the magic helper in Alpine
 * <div x-data="{ input: '' }">
 *   <input x-model="input" @blur="$validateSanitize(input)">
 * </div>
 *
 * @example
 * // Using the validator store
 * <div x-data="{ form: { name: '', email: '' } }">
 *   <button @click="$store.validator.validateForm(form)">Validate</button>
 * </div>
 */
document.addEventListener("alpine:init", () => {
  /**
   * Magic helper for inline validation and sanitization.
   * @function $validateSanitize
   * @param {string} text - Text to validate
   * @param {SanitizeOptions} [options] - Validation options
   * @returns {ValidationResult}
   */
  Alpine.magic("validateSanitize", () => {
    return (text, options) => InputSanitizer.validateAndSanitize(text, options);
  });

  /**
   * Validator store providing form validation utilities.
   * @typedef {Object} ValidatorStore
   * @property {function(string, SanitizeOptions): ValidationResult} validate - Validate single value
   * @property {function(Object, Object): FormValidationResult} validateForm - Validate entire form
   */
  Alpine.store("validator", {
    /**
     * Validates a single text value.
     * @param {string} text - The text to validate
     * @param {SanitizeOptions} [options] - Validation options
     * @returns {ValidationResult}
     */
    validate: (text, options) => {
      return InputSanitizer.validateAndSanitize(text, options);
    },

    /**
     * Validates an entire form's data object.
     * @param {Object<string, string>} formData - Form data as key-value pairs
     * @param {Object<string, SanitizeOptions>} [rules={}] - Per-field validation rules
     * @returns {FormValidationResult}
     *
     * @example
     * const result = $store.validator.validateForm(
     *   { name: 'John', email: 'john@example.com' },
     *   { name: { minLength: 2 }, email: { minLength: 5 } }
     * );
     */
    validateForm: (formData, rules = {}) => {
      /** @type {Object<string, ValidationResult>} */
      const results = {};
      let isValid = true;

      for (const [key, value] of Object.entries(formData)) {
        if (InputSanitizer.shouldSkipField(key)) {
          continue;
        }

        const fieldRules = rules[key] || {};
        const result = InputSanitizer.validateAndSanitize(value, fieldRules);

        results[key] = result;
        if (!result.isValid) {
          isValid = false;
        }
      }

      return { isValid, results };
    },
  });
});

/**
 * Native Form Submission Handler - Validates non-HTMX forms on submit.
 *
 * @listens submit
 *
 * @description
 * This handler:
 * 1. Skips forms with data-no-sanitize attribute
 * 2. Skips HTMX-handled forms (those with hx-post or hx-get)
 * 3. Validates text inputs, email inputs, textareas, and name fields
 * 4. Skips individual inputs with data-no-sanitize attribute
 * 5. Respects input maxLength and required attributes
 * 6. Blocks submission and shows errors if validation fails
 *
 * @example
 * // Standard form - will be validated
 * <form action="/submit" method="POST">
 *   <input type="text" name="username" required>
 *   <button type="submit">Submit</button>
 * </form>
 *
 * @example
 * // Form with sanitization disabled
 * <form action="/submit" method="POST" data-no-sanitize>
 *   <textarea name="html_content"></textarea>
 *   <button type="submit">Submit</button>
 * </form>
 *
 * @example
 * // Single field with sanitization disabled
 * <form action="/submit" method="POST">
 *   <input type="text" name="username" required>
 *   <textarea name="code_snippet" data-no-sanitize></textarea>
 *   <button type="submit">Submit</button>
 * </form>
 */
document.addEventListener("submit", function (event) {
  const form = /** @type {HTMLFormElement} */ (event.target);

  // Skip if form has data-no-sanitize OR if HTMX is handling this form
  if (
    form.hasAttribute("data-no-sanitize") ||
    form.hasAttribute("hx-post") ||
    form.hasAttribute("hx-get")
  ) {
    return;
  }

  /** @type {NodeListOf<HTMLInputElement|HTMLTextAreaElement>} */
  const inputs = form.querySelectorAll(
    'input[type="text"], input[type="email"], textarea, input[name*="name"]',
  );

  let hasError = false;
  /** @type {string[]} */
  const errors = [];

  inputs.forEach((input) => {
    // Skip inputs with data-no-sanitize attribute
    if (input.hasAttribute("data-no-sanitize")) {
      return;
    }

    // Skip technical fields by name
    if (InputSanitizer.shouldSkipField(input.name)) {
      return;
    }

    // Skip empty non-required fields
    if (!input.required && !input.value) {
      return;
    }

    if (input.value) {
      const maxLen = input.maxLength > 0 ? input.maxLength : null;

      const result = InputSanitizer.validateAndSanitize(input.value, {
        maxLength: maxLen,
        allowEmpty: !input.required,
      });

      if (!result.isValid) {
        hasError = true;
        errors.push(`${input.name || "Field"}: ${result.error}`);
        console.warn(
          `[InputSanitizer] Validation failed for "${input.name}":`,
          input.value,
        );
      } else {
        input.value = result.sanitized;
      }
    }
  });

  if (hasError) {
    event.preventDefault();
    alert("Form validation failed:\n" + errors.join("\n"));
  }
});

// Export for module systems (if applicable)
if (typeof module !== "undefined" && module.exports) {
  module.exports = InputSanitizer;
}
